import React, { useState } from 'react';
import { Avatar, Popconfirm, Tooltip, Typography, message } from 'antd';
import { CheckCircleFilled, LinkOutlined, ExportOutlined, CloseOutlined } from '@ant-design/icons';

import { getPayerBaseUrl } from '../../utils/URLBuilder';
import { useTemplatePartners } from '../../models/useTemplatePartners';
import { useTeamMemberIds } from '../../models/useTeamMemberIds';
import { unbindMemberId, unbindErrorMessages, tagIdForBinding } from '../../models/unbindMemberId';

import { useTeamAccess } from './useTeamAdminAccess';

const { Text } = Typography;

/** tagId is {templateTagId}---{tagUid}; returns the tagUid part (null when not parseable). */
export function tagUidFromTagId(tagId) {
    if (!tagId || typeof tagId !== 'string' || !tagId.includes('---')) return null;
    return tagId.split('---').slice(1).join('---') || null;
}

/** The member's live tipping page for a specific binding (payer host, xmid pre-routed). */
export function tippingPageUrlForBinding(binding) {
    if (!binding || !binding.tagId || !binding.xmid) return null;
    return `${getPayerBaseUrl()}/tags/${binding.tagId}?xmid=${encodeURIComponent(binding.xmid)}`;
}

/**
 * Subtle "connected to a partner integration" indicator: the integration's brand
 * image with a superscript green check overlaid on its top-right corner (flag
 * style). Falls back to a link icon with the same corner check when nothing
 * resolves. Never shows the raw xmid.
 *
 * The image is the TEMPLATE's signup image where set — that is the mark the
 * member saw when they claimed the tag they are holding, so it is the one they
 * recognise. The partner's own avatar is the company logo and may mean nothing to
 * them; it is the fallback, not the first choice. Always a circle: these images
 * are banners and would distort if stretched to fill a square.
 */
export const ConnectedPartnerBadge = ({ partner, size = 20, xmid = null, ...rest }) => {
    const base = partner && partner.displayName
        ? `Connected to ${partner.displayName}`
        : 'Connected integration';
    // Admin surfaces pass the xmid so the rollover reveals it; owner surfaces don't.
    const title = xmid ? `${base} (${xmid})` : base;
    const checkSize = Math.max(10, Math.round(size * 0.55));

    return (
        <Tooltip title={title} {...rest}>
            <span
                style={{
                    position: 'relative',
                    display: 'inline-block',
                    width: `${size}px`,
                    height: `${size}px`,
                    verticalAlign: 'middle',
                }}
            >
                {partner && (partner.brandImage || partner.avatar)
                    ? (
                        <Avatar
                            size={size}
                            shape="circle"
                            src={partner.brandImage || partner.avatar}
                        />
                    )
                    : (
                        <Avatar
                            size={size}
                            icon={<LinkOutlined />}
                            style={{ backgroundColor: '#f0f0f0', color: '#1890ff' }}
                        />
                    )}
                <CheckCircleFilled
                    style={{
                        position: 'absolute',
                        top: `-${Math.round(checkSize / 3)}px`,
                        right: `-${Math.round(checkSize / 3)}px`,
                        fontSize: `${checkSize}px`,
                        color: '#52c41a',
                        background: '#fff',
                        borderRadius: '50%',
                    }}
                />
            </span>
        </Tooltip>
    );
};

/**
 * Per-bind detail row (Strike Admin + member details):
 * [partner avatar with corner check] templateTagId · tagUid · xmid, with a
 * clickable link that opens the member's live tipping page for this binding.
 */
export const ConnectedPartnerAdminLine = ({
    templateTagId, xmid, binding, partner, compact = false, onRelease = null, releasing = false,
}) => {
    const tagUid = tagUidFromTagId(binding && binding.tagId);
    const tippingUrl = tippingPageUrlForBinding(binding ? { ...binding, xmid: binding.xmid || xmid } : null);

    // Compact form (admin team member LIST): values only — the admin knows what
    // they are — with the labelled breakdown on rollover and NO link-out (the
    // tipping-page link lives on the member detail page only).
    if (compact) {
        const values = [templateTagId, tagUid, xmid].filter(Boolean).join(' \u00b7 ');
        const rollover = [
            templateTagId ? `template ${templateTagId}` : null,
            tagUid ? `uid ${tagUid}` : null,
            xmid ? `xmid ${xmid}` : null,
        ].filter(Boolean).join(' \u00b7 ');
        return (
            <Tooltip title={rollover}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', maxWidth: '100%', minWidth: 0 }}>
                    <ConnectedPartnerBadge partner={partner} size={18} />
                    <Text
                        type="secondary"
                        style={{
                            fontSize: '12px',
                            fontFamily: 'monospace',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            minWidth: 0,
                        }}
                    >
                        {values}
                    </Text>
                </span>
            </Tooltip>
        );
    }

    const cell = (label, value, mono = true) => (value ? (
        <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: '3px' }}>
            <Text type="secondary" style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</Text>
            <Text style={{ fontSize: '12px', fontFamily: mono ? 'monospace' : undefined }}>{value}</Text>
        </span>
    ) : null);

    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '4px 8px',
                background: '#fafafa',
                border: '1px solid #f0f0f0',
                borderRadius: '6px',
                width: 'fit-content',
            }}
        >
            <ConnectedPartnerBadge partner={partner} size={22} />
            {cell('template', templateTagId)}
            {cell('uid', tagUid)}
            {cell('xmid', xmid)}
            {tippingUrl && (
                <Tooltip title="Open this member's tipping page">
                    <a href={tippingUrl} target="_blank" rel="noopener noreferrer" aria-label="Open tipping page">
                        <ExportOutlined style={{ fontSize: '12px' }} />
                    </a>
                </Tooltip>
            )}
            {onRelease && (
                <Popconfirm
                    title={(
                        <>
                            {`Release ${xmid} from this member?`}
                            <br />
                            {/* Re-linking needs the partner's join link, not a
                                button here — say so, or the release looks
                                irreversible when it is not. */}
                            <Text type="secondary">
                                They can be linked again by resending the join link.
                            </Text>
                        </>
                    )}
                    okText="Release"
                    cancelText="Cancel"
                    okButtonProps={{ danger: true, loading: releasing }}
                    onConfirm={onRelease}
                >
                    <CloseOutlined
                        style={{ fontSize: '12px', cursor: 'pointer' }}
                        aria-label={`Release ${xmid}`}
                    />
                </Popconfirm>
            )}
        </div>
    );
};

/**
 * A member's integration bindings as a vertical list of admin lines —
 * used on the member details ("staff member") screen and admin surfaces.
 * Loads the team's memberIds bindings (for tagUid + the tipping-page link)
 * and the partner avatars, both cached/one-shot.
 */
export const MemberBindingsList = ({ teamId, externalIds, actingClientId = null }) => {
    const entries = Object.entries(externalIds || {});
    const partners = useTemplatePartners(entries.map(([templateTagId]) => templateTagId));
    const bindings = useTeamMemberIds(teamId, entries.length > 0);
    const [releasing, setReleasing] = useState(null);

    // Who may release, decided here rather than by a prop: this is the same check
    // the API makes — team owner, team manager, or Strike admin — so the button
    // appears exactly when the release behind it would be allowed. A plain member
    // never sees it, and would be refused if they reached the endpoint anyway.
    const { allowed: canUnbind } = useTeamAccess(teamId, { require: 'manager' });

    // Release goes through the strike-payer API, never a direct Firestore delete:
    // the API is what emits member_removed to the partner and clears the member
    // doc's externalIds mirror. A direct delete would leave the partner believing
    // the ID is still in use, and a stale mirror key silently no-ops a re-join.
    //
    // There is deliberately no "add" counterpart. An xmid only ever arrives on a
    // partner's integration link, so binding is self-validating; typing one by
    // hand cannot be checked, and a transposed digit routes a member's tips to
    // somebody else. Repair is: release, then resend the join link.
    const release = async (binding, templateTagId, xmid) => {
        // No binding doc behind the externalIds row: it was already released and
        // only the mirror is stale. Nothing to delete — say so rather than 404.
        if (!binding) {
            message.error(unbindErrorMessages['binding-not-found']);
            return;
        }
        setReleasing(`${templateTagId}---${xmid}`);
        // The API is addressed by tag, as join is — the binding row carries the
        // tagId (with a same-template sibling as fallback for repaired rows).
        // actingClientId says which client is releasing. The server verifies it
        // against ownerId and judges team standing from it — a Strike admin needs
        // none, so it is optional rather than required.
        const result = await unbindMemberId({
            tagId: tagIdForBinding(binding, bindings),
            xmid,
            clientId: actingClientId,
        });
        setReleasing(null);
        if (result.ok) {
            // Nothing to update here — the memberIds subscription and the member
            // doc's externalIds both change at source, so the row goes when
            // Firestore says it has, never on optimism.
            message.success(`Released ${xmid}`);
            return;
        }
        message.error(unbindErrorMessages[result.error] || 'Could not release that ID.');
    };

    if (entries.length === 0) return null;
    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
            {entries.map(([templateTagId, xmid]) => {
                const binding = (bindings || []).find((b) => b.id === `${templateTagId}---${xmid}`);
                return (
                    <ConnectedPartnerAdminLine
                        key={templateTagId}
                        templateTagId={templateTagId}
                        xmid={xmid}
                        binding={binding}
                        partner={partners ? partners[templateTagId] : null}
                        onRelease={canUnbind ? () => release(binding, templateTagId, xmid) : null}
                        releasing={releasing === `${templateTagId}---${xmid}`}
                    />
                );
            })}
        </div>
    );
};
